import { CheckImpersonation, HasPermission } from '@shared/components';
import { useHasPermission, useCurrentUser } from '@shared/hooks';
import { useGetDashboard } from '@pages/dashboard/hooks';
import { Typography, Button } from '@ui/components';
import { Cancel, Plus } from '@ui/components/icons';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ssManager } from '@shared/utils';
import { Link } from 'react-router-dom';
import { Routes } from '@shared/enums';
import { cn } from '@ui/utils';

interface DashboardWelcomeHeroProps extends React.ComponentPropsWithoutRef<'div'> {
  onTopUpClick: () => void;
}

export const DashboardWelcomeHero = (props: DashboardWelcomeHeroProps) => {
  const { className, onTopUpClick, ...rest } = props;

  const [isDashboardWelcomeHeroVisible, setIsDashboardWelcomeHeroVisible] = useState(false);

  const { data, isLoading } = useGetDashboard();
  const { user } = useCurrentUser();
  const { t } = useTranslation();

  const hasEmployeesPermission = useHasPermission('DASHBOARD_EMPLOYEES_ADD_NEW', 'WRITE');
  const hasVehiclesPermission = useHasPermission('DASHBOARD_VEHICLES_ADD_NEW', 'WRITE');
  const hasTopUpPermission =
    useHasPermission('DASHBOARD_ACCOUNT_TOP_UP', 'WRITE') && !!user?.impersonator?.id;

  const hasEmployees = data?.numberOfEmployees;
  const hasVehicles = data?.numberOfVehicles;
  const hasAnyTopUps = !!data?.topUps?.length;

  const handleCloseBalance = () => {
    ssManager.set('DASHBOARD_WELCOME_HERO_OPEN', 'false');
    setIsDashboardWelcomeHeroVisible(false);
  };

  useEffect(() => {
    setIsDashboardWelcomeHeroVisible(ssManager.flag('DASHBOARD_WELCOME_HERO_OPEN', true));
  }, []);

  if (isLoading || !isDashboardWelcomeHeroVisible) {
    return null;
  }

  const hasUsefulEmployeePermission = !hasEmployees && hasEmployeesPermission;
  const hasUsefulVehiclePermission = !hasVehicles && hasVehiclesPermission;
  const hasUsefulTopUpPermission = !hasAnyTopUps && hasTopUpPermission && !user?.impersonator;

  const hasAnyUsefulPermission =
    hasUsefulEmployeePermission || hasUsefulVehiclePermission || hasUsefulTopUpPermission;

  if (!hasAnyUsefulPermission) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-between bg-surface rounded-xl p-8 text-contrast relative',
        className,
      )}
      {...rest}
    >
      <Typography className="mb-12" variant="h400">
        {t('DASHBOARD.WELCOME_HERO.TITLE')}
      </Typography>

      <div className="flex gap-16 justify-between">
        {!hasAnyTopUps && (
          <CheckImpersonation>
            <HasPermission permissionFeature="DASHBOARD_ACCOUNT_TOP_UP" permissionType="WRITE">
              <div className="flex items-center gap-6 cursor-pointer" onClick={onTopUpClick}>
                <Typography variant="h200">{t('DASHBOARD.WELCOME_HERO.ADD_BALANCE')}</Typography>
                <Button variant="secondary" size="small" icon>
                  <Plus />
                </Button>
              </div>
            </HasPermission>
          </CheckImpersonation>
        )}

        {!hasEmployees && (
          <HasPermission permissionFeature="DASHBOARD_EMPLOYEES_ADD_NEW" permissionType="WRITE">
            <Typography variant="h200" asChild>
              <Link
                state={{ openEmployeeDrawer: true }}
                className="flex items-center gap-6"
                to={`../${Routes.EMPLOYEES}`}
              >
                {t('DASHBOARD.WELCOME_HERO.ADD_EMPLOYEE')}
                <Button variant="secondary" size="small" icon>
                  <Plus />
                </Button>
              </Link>
            </Typography>
          </HasPermission>
        )}

        {!hasVehicles && (
          <HasPermission permissionFeature="DASHBOARD_VEHICLES_ADD_NEW" permissionType="WRITE">
            <Typography variant="h200" asChild>
              <Link
                state={{ openVehicleDrawer: true }}
                className="flex items-center gap-6"
                to={`../${Routes.VEHICLES}`}
              >
                {t('DASHBOARD.WELCOME_HERO.ADD_VEHICLE')}
                <Button variant="secondary" size="small" icon>
                  <Plus />
                </Button>
              </Link>
            </Typography>
          </HasPermission>
        )}
      </div>

      <Button className="absolute top-0 right-0 m-4" onClick={handleCloseBalance} size="small" icon>
        <Cancel />
      </Button>
    </div>
  );
};
